import React, { useEffect, useRef, useState } from "react";
import { Grid, Typography, Box } from "@mui/material";
import theme from "Theme/AppTheme";
import AcxSwitch from "components/UI/AcxSwitch";
import AcxButton from "components/UI/AcxButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface ControlsContainerProps {
    heading: string;
    description: string;
    children: React.ReactNode;
    isEnabled: boolean;
    setIsEnabled: (isEnabled: boolean) => void;
    maxHeight?: number;
    expandLabel?: string;
    collapseLabel?: string;
    shouldStartExpanded?: boolean;
    isViewOnly: boolean;
}

const ControlsContainer: React.FC<ControlsContainerProps> = ({
    heading,
    description,
    children,
    isEnabled,
    setIsEnabled,
    maxHeight = 250,
    expandLabel = "Volume Control",
    collapseLabel = "Volume Control",
    shouldStartExpanded = true,
    isViewOnly,
}) => {
    const contentRef = useRef<HTMLDivElement>(null);

    const [boxHeight, setBoxHeight] = useState("0px");
    const [isExpanded, setIsExpanded] = useState(shouldStartExpanded);

    useEffect(() => {
        setIsExpanded(isEnabled);
    }, [isEnabled]);

    useEffect(() => {
        const contentElement = contentRef.current;

        const updateBoxHeight = () => {
            if (!contentElement) return;

            // The box should not have a max height smaller than 250px
            setBoxHeight(
                isExpanded
                    ? `${Math.max(contentElement.scrollHeight, maxHeight)}px`
                    : "0px",
            );
        };

        updateBoxHeight();

        if (!contentElement) return;

        const resizeObserver = new ResizeObserver(updateBoxHeight);
        resizeObserver.observe(contentElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isExpanded, maxHeight, children]);

    useEffect(() => {
        if (!isExpanded) {
            return;
        }

        const animationFrame = requestAnimationFrame(() => {
            const contentElement = contentRef.current;

            if (!contentElement) return;

            setBoxHeight(
                `${Math.max(contentElement.scrollHeight, maxHeight)}px`,
            );
        });

        return () => cancelAnimationFrame(animationFrame);
    }, [children, isExpanded, maxHeight]);

    return (
        <Grid container item xs={12} alignItems={"flex-start"}>
            <Grid
                item
                xs={2}
                sx={{
                    padding: theme.spacing(2),
                    fontWeight: 600,
                    fontSize: "14px",
                }}
            >
                {heading}
            </Grid>
            <Grid
                item
                xs={7}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    padding: 2,
                }}
            >
                <Typography
                    sx={{
                        fontSize: "14px",
                        color: theme.palette.gray.main,
                    }}
                >
                    {description}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        maxHeight: boxHeight,
                        transition: "max-height 0.3s ease",
                    }}
                >
                    <Box
                        ref={contentRef}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            </Grid>
            <Grid
                item
                xs={2}
                sx={{
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <AcxSwitch
                    checked={isEnabled}
                    onChange={(_, checked) => setIsEnabled(checked)}
                    disabled={isViewOnly}
                />
                <AcxButton
                    sx={{
                        background: "transparent",
                        color: theme.palette.primary.main,
                        ":disabled": {
                            background: "transparent",
                            color: theme.palette.gray[400],
                        },
                    }}
                    disabled={!isEnabled}
                    onClick={() => setIsExpanded(!isExpanded)}
                    startIcon={
                        <KeyboardArrowDownIcon
                            sx={{
                                transform: isExpanded
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                                transition: "transform 0.3s ease",
                            }}
                        />
                    }
                >
                    {isExpanded ? collapseLabel : expandLabel}
                </AcxButton>
            </Grid>
        </Grid>
    );
};

export default ControlsContainer;
